import type { IInvoice } from '@/common/interfaces/invoice.interface';
import type { IAddress, IOrderItem } from '@/common/interfaces/order.interface';
import type { IOrderDetailStoreShare } from '@/modules/Orders/OrderDetail/OrderDetail';
import type { IParticipantHqAddress, IParticipantHqOrderDetail } from '@/api/ParticipantHqQuery';
import { MostRecentActivityStatus } from '@/modules/MostRecent/MostRecentActivity';
import {
  PARTICIPANT_FAILED_ORDER_STATUS_MESSAGE,
  PARTICIPANT_NOT_TIPPED_STATUS_MESSAGE,
} from '@/modules/Orders/OrderDetail/constants/orderDetailPageView.constants';
import { STORE_SHIPPING_OPTION_TYPES } from '@/modules/Orders/CreateOrders/StoreCheckout/constants/storeCheckout.constants';

const toAddress = (address: IParticipantHqAddress): IAddress => ({
  shippingName: address.shippingName,
  street1: address.street1,
  street2: address.street2,
  city: address.city,
  state: address.state,
  zipCode: address.zipCode,
  country: address.country,
});

/**
 * Picks the address a participant should see for their order.
 * Bulk stores ship everyone to a single destination held on the order, so the
 * participant's own row address would be wrong there; individual stores ship to
 * each participant. Falls back to the participant address whenever the order
 * has no bulk destination, so older orders keep rendering as before.
 * Used by useParticipantOrderDetail for the participant order page and Most Recent.
 */
export const mapParticipantDetailAddress = (order: IParticipantHqOrderDetail): IAddress => {
  const isBulkShipping = order.shippingType === STORE_SHIPPING_OPTION_TYPES.BULK_SHIPPING;

  if (isBulkShipping && order.orderAddress) {
    return toAddress(order.orderAddress);
  }

  return toAddress(order.address);
};

export const mapParticipantDetailOrderItems = (order: IParticipantHqOrderDetail): IOrderItem[] =>
  order.orderItems.map((item) => ({
    id: item.id,
    orderItemId: item.orderItemId,
    status: item.status,
    sizes: item.sizes,
    quantity: item.quantity,
    price: item.price,
    trackingNumber: item.trackingNumber || '',
    regOrderTrackLink: item.regOrderTrackLink || '',
    numUnits: item.numUnits,
    productTitle: item.productTitle,
    primaryImage: item.primaryImage,
    secondaryImage: item.secondaryImage,
    apparel: {
      name: item.apparel.name,
      brand: item.apparel.brand,
      styleCode: item.apparel.styleCode,
      color: item.apparel.color,
      hexCode: item.apparel.hexCode,
    } as IOrderItem['apparel'],
    design: {
      id: item.design.id,
      primaryImage: item.design.primaryImage || item.primaryImage || '',
      secondaryImage: item.design.secondaryImage || '',
      apparelStyleCode: item.design.apparelStyleCode,
      apparelColor: item.design.apparelColor,
      // HQ OrderItem expects nested { printType: { id } } rows; participant-hq
      // returns flat DB print_type_info — omit so badges fall back safely.
      printTypeInfo: undefined,
      decorMethodInfo: item.design.decorMethodInfo,
    } as IOrderItem['design'],
  }));

export const mapParticipantDetailInvoice = (order: IParticipantHqOrderDetail): IInvoice => {
  const items = order.orderItems.map((item, index) => ({
    invoiceItemId: item.id || index + 1,
    orderItemId: Number(item.orderItemId) || item.id || index + 1,
    alphaNumericOrderItemId: item.orderItemId,
    styleCode: item.apparel.styleCode || '',
    quantity: item.quantity,
    unitPrice: item.price,
    imageUrl: item.primaryImage || '',
    discount: 0,
  }));

  return {
    paymentReceived: order.summary.orderTotal,
    paymentExpected: order.summary.orderTotal,
    dueDate: order.dueDate || '',
    shipping: order.summary.shipping,
    salesTax: order.summary.salesTax,
    discount: order.summary.discount,
    invoiceId: order.orderNumber || order.participantId,
    items,
    fees: [],
    discountNew: [],
    shippingType: 'standard',
  };
};

/**
 * Header banner for a participant order.
 * A failed minimum replaces the live "keep sharing" copy; tipped orders use the
 * status header instead of an override.
 * Used by the participant order page and Most Recent.
 */
export const resolveParticipantHeaderStatusMessage = (
  isTipped: boolean,
  isFailedOrder: boolean
): string | undefined => {
  if (isFailedOrder) {
    return PARTICIPANT_FAILED_ORDER_STATUS_MESSAGE;
  }

  if (!isTipped) {
    return PARTICIPANT_NOT_TIPPED_STATUS_MESSAGE;
  }

  return undefined;
};

export const resolveParticipantDetailStatus = (
  order: IParticipantHqOrderDetail
): MostRecentActivityStatus => {
  const values = Object.values(MostRecentActivityStatus) as string[];
  if (order.statusKey && values.includes(order.statusKey)) {
    return order.statusKey as MostRecentActivityStatus;
  }
  return order.storeTipped
    ? MostRecentActivityStatus.inStudio
    : MostRecentActivityStatus.failedGroupOrder;
};

/**
 * Share card data for a participant's store, or null when the store can no longer
 * be shared (tipped, past its close date, closed permanent store) or the API
 * predates the store fields. Always points at the parent store, so a
 * permanent-store buyer can keep sharing while their own order is in production.
 * Used by useParticipantOrderDetail for the participant order page and Most Recent.
 */
export const mapParticipantStoreShare = (
  order: IParticipantHqOrderDetail
): IOrderDetailStoreShare | null => {
  if (!order.storeLive || !order.storeId) {
    return null;
  }

  return {
    storeId: String(order.storeId),
    groupOrderDate: order.groupOrderDate || undefined,
    isPermanentStore: Boolean(order.isPermanentStore),
  };
};

/**
 * Restricts participant tracking to shipped/delivered views.
 * Used by participant home and order detail to mount the scoped tracking overlay.
 */
export const canParticipantTrackOrder = (
  status: MostRecentActivityStatus,
  hasTrackingLink: boolean
): boolean =>
  hasTrackingLink &&
  [MostRecentActivityStatus.outForDelivery, MostRecentActivityStatus.delivered].includes(status);
